const TEXT_OVERRIDES_KEY = "ekman_text_overrides";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const editableTags = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6", "SPAN", "A", "LI", "BUTTON", "DIV"]);

let cachedOverrides = null;
let hasFetchedOverrides = false;

export const getTextOverrides = () => {
  if (cachedOverrides) {
    return cachedOverrides;
  }
  if (typeof window === "undefined") {
    return {};
  }
  try {
    return JSON.parse(localStorage.getItem(TEXT_OVERRIDES_KEY) || "{}");
  } catch {
    return {};
  }
};

export const fetchTextOverrides = async () => {
  // Only fetch once per session
  if (hasFetchedOverrides) return cachedOverrides || getTextOverrides();
  hasFetchedOverrides = true;
  
  try {
    const response = await fetch(`${API}/text-overrides`);
    if (response.ok) {
      const data = await response.json();
      cachedOverrides = data.overrides || {};
      localStorage.setItem(TEXT_OVERRIDES_KEY, JSON.stringify(cachedOverrides));
      return cachedOverrides;
    }
  } catch {
    // Fall back to localStorage
  }
  return getTextOverrides();
};

export const saveTextOverride = async (key, value) => {
  if (typeof window === "undefined" || !key) {
    return;
  }

  const current = getTextOverrides();
  current[key] = value;
  cachedOverrides = current;
  localStorage.setItem(TEXT_OVERRIDES_KEY, JSON.stringify(current));

  // Save to backend for cross-device sync
  try {
    await fetch(`${API}/text-overrides`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ overrides: current }),
    });
  } catch {
    // Saved locally, will sync later
  }
};

export const applyTextOverridesToDom = (root = document) => {
  const overrides = getTextOverrides();
  Object.entries(overrides).forEach(([testId, text]) => {
    const element = root.querySelector(`[data-testid="${testId}"]`);
    if (!element) {
      return;
    }
    if (!editableTags.has(element.tagName)) {
      return;
    }
    if (element.childElementCount > 0 && element.tagName === "DIV") {
      return;
    }
    element.textContent = text;
  });
};

export const attachAdminInlineTextEditing = (root = document) => {
  const onDoubleClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const element = target.closest("[data-testid]");
    if (!element || !(element instanceof HTMLElement)) {
      return;
    }

    if (!editableTags.has(element.tagName)) {
      return;
    }
    if (element.dataset.noTextEdit === "true") {
      return;
    }
    if (element.childElementCount > 0 && element.tagName === "DIV") {
      return;
    }

    const testId = element.dataset.testid;
    if (!testId) {
      return;
    }

    element.setAttribute("contenteditable", "true");
    element.focus();
    element.classList.add("outline", "outline-1", "outline-black");

    const stopEditing = () => {
      const updatedText = element.textContent?.trim() || "";
      saveTextOverride(testId, updatedText);
      element.removeAttribute("contenteditable");
      element.classList.remove("outline", "outline-1", "outline-black");
      element.removeEventListener("blur", stopEditing);
      element.removeEventListener("keydown", handleKeyDown);
    };

    const handleKeyDown = (keyEvent) => {
      if (keyEvent.key === "Enter") {
        keyEvent.preventDefault();
        element.blur();
      }
    };

    element.addEventListener("blur", stopEditing);
    element.addEventListener("keydown", handleKeyDown);
  };

  root.addEventListener("dblclick", onDoubleClick);
  return () => root.removeEventListener("dblclick", onDoubleClick);
};
